import type { Routes } from '@angular/router';
import { DashboardComponent } from './core/layout/pages/dashboard/dashboard.component';
import { CycleDetailsComponent } from './features/cycle-details/cycle-details.component';
import { ViewManagerCycleFormComponent } from './features/view-manager-cycle-form/view-manager-cycle-form.component';
import { SubManagerCyclesComponent } from './features/manager-dashboard/sub-manager-cycles/sub-manager-cycles.component';
import { SubManagerCycleFormsComponent } from './features/manager-dashboard/sub-manager-cycle-forms/sub-manager-cycle-forms.component';
import { subManagerCyclesResolver, subManagerCycleFormsResolver } from './features/manager-dashboard/resolvers';
import { AccessDeniedComponent } from './shared/components/access-denied/access-denied.component';
import { authGuard } from './guards/auth.guard';
import { permissionGuard, managerCycleFormGuard } from './guards/permission.guard';

export const routes: Routes = [
  // ========================================
  // ROOT REDIRECT
  // ========================================
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  },

  // ========================================
  // PUBLIC ROUTES
  // ========================================
  {
    path: 'access-denied',
    component: AccessDeniedComponent,
    title: 'Access Denied - BeFair Platform'
  },

  // ========================================
  // PROTECTED APPLICATION ROUTES
  // All routes below require authentication
  // ========================================
  {
    path: '',
    canActivate: [authGuard],
    children: [
      // ----------------------------------
      // DASHBOARD (Base landing page)
      // ----------------------------------
      {
        path: 'dashboard',
        component: DashboardComponent,
        title: 'Dashboard - BeFair Platform'
      },

      // ----------------------------------
      // CYCLE MANAGEMENT MODULE
      // ----------------------------------
      {
        path: 'create-cycle',
        loadChildren: () => import('./features/create-cycle/create-cycle.routes').then(m => m.createCycleRoutes),
        canActivate: [permissionGuard],
        data: {
          requiredPrivileges: ['CYCLE_MANAGEMENT'],
          module: 'Cycle Management'
        }
      },
      {
        path: 'edit-cycle',
        loadChildren: () => import('./features/edit-cycle/edit-cycle.routes').then(m => m.editCycleRoutes),
        canActivate: [permissionGuard],
        data: {
          requiredPrivileges: ['CYCLE_MANAGEMENT'],
          module: 'Cycle Management'
        }
      },
      {
        path: 'view-cycles',
        loadChildren: () => import('./features/view-cycles/view-cycles.routes').then(m => m.viewCyclesRoutes),
        canActivate: [permissionGuard],
        data: {
          requiredPrivileges: ['VIEW_CYCLE'],
          module: 'Cycle Management'
        }
      },
      {
        path: 'cycle-details/:id',
        component: CycleDetailsComponent,
        title: 'Cycle Details - BeFair Platform',
        canActivate: [permissionGuard],
        data: {
          requiredPrivileges: ['VIEW_CYCLE'],
          module: 'Cycle Management'
        }
      },

      // ----------------------------------
      // COMPLIANCE RECORDS MODULE
      // ----------------------------------
      {
        path: 'compliance-records',
        loadChildren: () => import('./features/compliance-records/compliance-records.routes').then(m => m.complianceRecordsRoutes),
        canActivate: [permissionGuard],
        data: {
          requiredPrivileges: ['VIEW_CYCLE'],
          module: 'Compliance'
        }
      },

      // ----------------------------------
      // FORM MANAGEMENT MODULE
      // ----------------------------------
      {
        path: 'manage-form',
        loadChildren: () => import('./features/manage-form/manage-form.routes').then(m => m.manageFormRoutes),
        canActivate: [permissionGuard],
        data: {
          requiredPrivileges: ['FORM_MANAGEMENT'],
          module: 'Form Management'
        }
      },

      // ----------------------------------
      // MANAGER MODULE
      // ----------------------------------
      {
        path: 'manager-form',
        loadChildren: () => import('./features/manager-form/manager-form.routes').then(m => m.managerFormRoutes),
        canActivate: [permissionGuard],
        data: {
          requiredPrivileges: ['MANAGER_DASHBOARD'],
          module: 'Manager Dashboard'
        }
      },
      {
        path: 'sub-manager-cycles',
        component: SubManagerCyclesComponent,
        title: 'Sub-Manager Cycles - BeFair Platform',
        canActivate: [permissionGuard],
        resolve: {
          resolvedData: subManagerCyclesResolver
        },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        data: {
          requiredPrivileges: ['MANAGER_DASHBOARD'],
          module: 'Manager Dashboard'
        }
      },
      {
        path: 'sub-manager-cycle-forms',
        component: SubManagerCycleFormsComponent,
        title: 'Sub-Manager Cycle Forms - BeFair Platform',
        canActivate: [permissionGuard],
        resolve: {
          resolvedData: subManagerCycleFormsResolver
        },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        data: {
          requiredPrivileges: ['MANAGER_DASHBOARD'],
          module: 'Manager Dashboard'
        }
      },
      {
        path: 'manager-cycle-form/:cycleId/:managerId',
        component: ViewManagerCycleFormComponent,
        title: 'Manager Cycle Form - BeFair Platform',
        canActivate: [managerCycleFormGuard],
        data: {
          module: 'Manager Dashboard'
        }
      },

      // ----------------------------------
      // ADMINISTRATION MODULE
      // ----------------------------------
      {
        path: 'user-management',
        loadChildren: () => import('./features/user-management/user-management.routes').then(m => m.userManagementRoutes),
        canActivate: [permissionGuard],
        data: {
          requiredPrivileges: ['USER_MANAGEMENT'],
          module: 'Administration'
        }
      },
      {
        path: 'master-data',
        loadChildren: () => import('./features/master-data/master-data.routes').then(m => m.masterDataRoutes),
        canActivate: [permissionGuard],
        data: {
          requiredPrivileges: ['MASTER_DATA_MANAGEMENT'],
          module: 'Administration'
        }
      },

      // ----------------------------------
      // HELP & DOCUMENTATION
      // ----------------------------------
      {
        path: 'help-guide',
        loadChildren: () => import('./features/help-guide/help-guide.routes').then(m => m.helpGuideRoutes),
        title: 'Help Guide - BeFair Platform'
      },

      // ----------------------------------
      // CONTACT & SUPPORT
      // ----------------------------------
      {
        path: 'contact-support',
        loadChildren: () => import('./features/contact-support/contact-support.routes'),
        title: 'Contact & Support - BeFair Platform'
      },

      // ----------------------------------
      // LEGACY REDIRECTS (Backward Compatibility)
      // Added: November 2025
      // ----------------------------------
      {
        path: 'view-form',
        redirectTo: 'manage-form',
        pathMatch: 'full'
      },
      {
        path: 'edit-form',
        redirectTo: 'manage-form/edit',
        pathMatch: 'full'
      }
    ]
  },

  // ========================================
  // FALLBACK - Unknown Routes
  // ========================================
  {
    path: '**',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  }
];
